/**
 * Axios client factory for creating configured API clients
 */

import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

export interface CreateClientOptions {
  baseURL: string;
  includeTenantSlug?: boolean;
  getAccessToken?: () => string | null;
  getRefreshToken?: () => string | null;
  getTenantSlug?: () => string | null;
  onTokenRefresh?: (accessToken: string, refreshToken: string) => void;
  onAuthError?: () => void;
  refreshEndpoint?: string;
}

export function createApiClient(options: CreateClientOptions): AxiosInstance {
  const {
    baseURL,
    includeTenantSlug = false,
    getAccessToken,
    getRefreshToken,
    getTenantSlug,
    onTokenRefresh,
    onAuthError,
    refreshEndpoint,
  } = options;

  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add auth headers
  client.interceptors.request.use(
    (config) => {
      const token = getAccessToken?.();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (includeTenantSlug) {
        const slug = getTenantSlug?.();
        if (slug) {
          config.headers['X-Tenant-Slug'] = slug;
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle 401 and refresh token
  let isRefreshing = false;
  let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
  }> = [];

  const processQueue = (error: unknown = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    failedQueue = [];
  };

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as AxiosRequestConfig & {
        _retry?: boolean;
      };

      // Skip auth error handling for login endpoints — their 401 means "wrong credentials"
      const url = originalRequest.url || '';
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');

      if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => client(originalRequest))
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = getRefreshToken?.();
        if (!refreshToken || !refreshEndpoint || !onTokenRefresh) {
          isRefreshing = false;
          onAuthError?.();
          return Promise.reject(error);
        }

        try {
          const response = await axios.post(
            `${baseURL}${refreshEndpoint}`,
            { refresh_token: refreshToken },
            {
              headers: includeTenantSlug && getTenantSlug
                ? { 'X-Tenant-Slug': getTenantSlug() }
                : undefined,
            }
          );

          const { access_token, refresh_token } = response.data;
          onTokenRefresh(access_token, refresh_token);
          window.dispatchEvent(new CustomEvent('intellimon:token-refreshed'));

          processQueue();
          return client(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          onAuthError?.();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
}
